// src/oura/components/client/PackSheet.tsx
//
// A pack in the staff drawer (drawer specs v1.1, A-L). A pack is the batch of
// questions Angel sends the client in one email and one notice, with a
// covering note and a reference (OT-2026-NYO-002). This sheet shows its
// questions, each in the group it sits in now and opening as a sub-view, and
// the note folded away. A draft has one move: Publish, which sends it.
//
// Opened from a question's "Sent in {pack}" line, as a sub-view with "‹
// {question}" to go back. The Packs card is gone (YW, 29 Sep 2026: new
// questions go through the Google Docs), so this sheet is the one place a
// sent pack is read, and Publish shows only for a draft reached by its link.
// The note is the merchant's own PackNoteBody, so staff read exactly what
// the client read.

import { useState } from "react";
import { PackageIcon } from "lucide-react";
import { publishPack, type Pack } from "@service/aeo/clientReview";
import { dayDate } from "../../lib/oura";
import {
    AREA_TEXT,
    DrawerBlock,
    DrawerFold,
    DrawerFoot,
    DrawerHead,
    Dot,
    ListRow,
    PillButton,
    Tag,
    type DrawerFootMessage,
    type DrawerStatus,
    type Tone,
} from "@oura/ui";
import { PackNoteBody } from "../../merchant/PackNote";
import { noteBlocks } from "../../merchant/asks";
import { flowDone, flowMessage, useSheetVoice, type SheetFlow } from "./drawerFlow";
import type { AskTarget } from "./askTargets";

export function PackSheet({
    pack,
    itemGroup,
    flow,
    onSub,
    onChanged,
}: {
    pack: Pack;
    /** The group a question sits in now, its word and dot tone; null when the lists do not place it. */
    itemGroup: (itemId: string) => { label: string; tone: Tone } | null;
    flow: SheetFlow;
    onSub: (t: AskTarget) => void;
    onChanged: () => Promise<void>;
}) {
    const [saidText, setSaid] = useState<string | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [busy, setBusy] = useState(false);
    /** The pack sent from here: Publish never shows again for it, even while `pack` is the copy read before it went. */
    const [publishedId, setPublishedId] = useState<string | null>(null);
    const voice = useSheetVoice(setSaid, setMessage);
    const draft = pack.status === "draft" && publishedId !== pack.id;
    const blocks = noteBlocks(pack);
    const sections = blocks.filter((b) => b.type === "section").length;

    const publish = async () => {
        if (busy || !draft) return;
        setBusy(true);
        flow.onBusy?.(true);
        setMessage(null);
        setSaid(null);
        try {
            await publishPack(pack.id);
        } catch {
            voice.note({ tone: "error", text: "The pack was not sent. Try again." });
            setBusy(false);
            flow.onBusy?.(false);
            return;
        }
        // It went: whatever the lists' re-read does, it is never offered again.
        setPublishedId(pack.id);
        try {
            await onChanged();
            voice.said("Published to the client.");
        } catch {
            voice.note({ tone: "info", text: "Published to the client. The lists did not refresh: reload the page to see it there." });
        } finally {
            setBusy(false);
            flow.onBusy?.(false);
        }
    };

    const sent = dayDate(pack.publishedAt);
    const status: DrawerStatus = draft
        ? { tone: "blue", label: "Not sent yet", clock: { text: "The client can't see it" } }
        : { tone: "green", label: "Sent", clock: sent ? { text: sent } : null };
    const done = flowDone(flow, saidText);
    const footMessage = flowMessage(done, message, saidText);

    return (
        <>
            <DrawerHead
                lead={<PackageIcon aria-hidden size={14} className={AREA_TEXT.asks} />}
                title={pack.title}
                status={status}
                chips={pack.ref ? <Tag tone="gray" className="tabular-nums">{pack.ref}</Tag> : undefined}
            />
            {pack.items.length > 0 && (
                <DrawerBlock label={`Questions · ${pack.items.length}`} busy={busy}>
                    <div className="mt-1">
                        {pack.items.map((it) => {
                            const g = itemGroup(it.id) ?? (draft ? { label: "Not sent yet", tone: "blue" as Tone } : null);
                            return (
                                <ListRow
                                    key={it.id}
                                    lead="dot"
                                    stackMeta
                                    data-pack-item-id={it.id}
                                    onClick={() => onSub({ kind: "item", itemId: it.id })}
                                    icon={<Dot tone={g?.tone ?? "gray"} />}
                                    title={it.title}
                                    meta={g?.label}
                                />
                            );
                        })}
                    </div>
                </DrawerBlock>
            )}
            {blocks.length > 0 && (
                <DrawerFold id="staff-pack.note" label="Note" note={sections > 0 ? `${sections} ${sections === 1 ? "section" : "sections"}` : undefined} remember>
                    {/* The merchant's own note body, read only: staff see what the client read. */}
                    <PackNoteBody pack={pack} />
                </DrawerFold>
            )}
            <DrawerFoot
                message={footMessage}
                done={done}
                first={
                    draft && !done ? (
                        <PillButton variant="primary" busy={busy} onClick={() => void publish()}>
                            Publish
                        </PillButton>
                    ) : undefined
                }
            />
        </>
    );
}
